import { Alert, Button, Collapse, Form, Input, Modal, QRCode, Result, Space, Steps, Typography } from "antd";

import { useAuth } from "../../contexts/Auth";
import { isPasskeySupported } from "../../utils/webauthn";
import ReauthenticateModal from "./ReauthenticateModal";
import { EnrollmentMethod, RECOVERY_COPY, useSecurityEnrollment } from "./useSecurityEnrollment";

interface SecurityEnrollmentModalProps {
    open: boolean;
    initialMethod?: EnrollmentMethod;
    onClose: () => void;
    onComplete: (factorType: EnrollmentMethod) => void;
}

export default function SecurityEnrollmentModal({ open, initialMethod, onClose, onComplete }: SecurityEnrollmentModalProps) {
    const { profile } = useAuth();
    const {
        method, choose, step, setStep,
        totpEnrollment, starting, startFailed, restarted, totpCountdown, code, setCode, verifying, startTotp, verifyTotp,
        passkeyName, setPasskeyName, registering, registerPasskey,
        error, setError, reauth,
    } = useSecurityEnrollment({ open, initialMethod, onComplete });
    const switchToPasskey = () => choose("passkey");

    const totpSteps = [
        {
            title: "Scan",
            content: (
                <Space direction="vertical" size="middle" style={{ width: "100%", alignItems: "center" }}>
                    {restarted && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ width: "100%" }}
                            message="This setup code expired. We've generated a new one - scan it again."
                        />
                    )}
                    <Typography.Paragraph style={{ marginBottom: 0 }}>
                        Scan this code with your authenticator app.
                    </Typography.Paragraph>
                    {totpEnrollment?.otpauthUri
                        ? <QRCode value={totpEnrollment.otpauthUri} size={192} />
                        : <Typography.Text type="secondary">Preparing your setup code...</Typography.Text>}
                    <Typography.Text type="secondary">Account: {profile?.email}</Typography.Text>
                    {totpEnrollment?.secret && (
                        <Collapse
                            ghost
                            style={{ width: "100%" }}
                            items={[{
                                key: "manual",
                                label: "Can't scan? Enter the key manually",
                                children: <Typography.Text code copyable>{totpEnrollment.secret}</Typography.Text>
                            }]}
                        />
                    )}
                    {totpCountdown && <Typography.Text type="secondary">Expires in {totpCountdown}</Typography.Text>}
                    <Button type="primary" block disabled={!totpEnrollment} onClick={() => { setError(undefined); setStep(1); }}>
                        Continue
                    </Button>
                </Space>
            )
        },
        {
            title: "Verify",
            content: (
                <Form layout="vertical" onFinish={() => verifyTotp()} style={{ width: "100%" }}>
                    <Typography.Paragraph>Enter the six-digit code your authenticator app shows.</Typography.Paragraph>
                    <Form.Item validateStatus={error ? "error" : undefined} help={error}>
                        <Input.OTP
                            length={6}
                            value={code}
                            onChange={value => { setCode(value); setError(undefined); }}
                            aria-label="Six-digit code from your authenticator app"
                            autoFocus
                        />
                    </Form.Item>
                    {totpCountdown && <Typography.Text type="secondary">Expires in {totpCountdown}</Typography.Text>}
                    <Button htmlType="submit" type="primary" block loading={verifying} disabled={code.length !== 6} style={{ marginTop: 16 }}>
                        Verify
                    </Button>
                    <Button type="link" block onClick={() => setStep(0)}>Back to the QR code</Button>
                </Form>
            )
        },
        {
            title: "Done",
            content: (
                <Result
                    status="success"
                    title="Two-step verification is on"
                    subTitle={RECOVERY_COPY}
                    extra={[
                        <Button key="passkey" onClick={switchToPasskey}>Add a passkey too</Button>,
                        <Button key="done" type="primary" onClick={onClose}>Done</Button>
                    ]}
                />
            )
        }
    ];

    const passkeySteps = [
        {
            title: "Name",
            content: (
                <Form layout="vertical" onFinish={() => { setError(undefined); setStep(1); }} style={{ width: "100%" }}>
                    <Form.Item label="Name this passkey" help="So you can tell your passkeys apart later.">
                        <Input
                            value={passkeyName}
                            maxLength={80}
                            autoFocus
                            onChange={event => setPasskeyName(event.target.value)}
                        />
                    </Form.Item>
                    <Button htmlType="submit" type="primary" block>Continue</Button>
                </Form>
            )
        },
        {
            title: "Register",
            content: (
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Alert type="info" showIcon message="Your browser will offer Face ID, Touch ID, device screen lock, a synced passkey, or a security key." />
                    {error && <Alert type="error" showIcon message={error} />}
                    <Button type="primary" block loading={registering} onClick={registerPasskey}>Continue with passkey</Button>
                    <Button type="link" block onClick={() => setStep(0)}>Rename this passkey</Button>
                </Space>
            )
        },
        {
            title: "Done",
            content: (
                <Result
                    status="success"
                    title="Passkey added"
                    subTitle={RECOVERY_COPY}
                    extra={[<Button key="done" type="primary" onClick={onClose}>Done</Button>]}
                />
            )
        }
    ];

    const steps = method === "passkey" ? passkeySteps : totpSteps;
    const title = method === "totp" ? "Set up an authenticator app" : method === "passkey" ? "Add a passkey" : "Add two-step verification";

    return (
        <>
            <Modal open={open} title={title} footer={null} onCancel={onClose} destroyOnHidden>
                {!method ? (
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <Typography.Paragraph type="secondary">
                            Add an authenticator app or a passkey. It takes about a minute.
                        </Typography.Paragraph>
                        <Button block size="large" onClick={() => choose("totp")}>Authenticator app</Button>
                        <Button block size="large" disabled={!isPasskeySupported()} onClick={switchToPasskey}>Passkey</Button>
                    </Space>
                ) : method === "totp" && startFailed ? (
                    <Result
                        status="warning"
                        title="We couldn't start setup"
                        extra={<Button type="primary" loading={starting} onClick={() => startTotp()}>Try again</Button>}
                    />
                ) : (
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <Steps
                            size="small"
                            current={step}
                            items={steps.map((item, index) => ({
                                title: item.title,
                                status: step > index ? "finish" : step === index ? "process" : "wait"
                            }))}
                        />
                        {steps[step].content}
                    </Space>
                )}
            </Modal>
            <ReauthenticateModal open={reauth.reauthenticating} onCancel={reauth.cancel} onSuccess={reauth.succeed} />
        </>
    );
}
